import * as Sentry from '@sentry/react'

export interface InitSentryBrowserOptions {
  dsn?: string
  environment?: string
  release?: string
  /** The API origin. When provided, distributed tracing is wired
   * (`browserTracingIntegration` + `tracePropagationTargets`) so fetches to
   * the API carry `sentry-trace`/`baggage` headers and backend events link to
   * the browser trace. Must be explicit — the api is a DIFFERENT origin and
   * the SDK only propagates same-origin by default. */
  apiBaseUrl?: string
}

/**
 * Initialize Sentry for the ADMIN client. Mirrors `apps/web`'s fail-closed
 * gate (ADR-0046): the DSN must be nonblank and the environment must be
 * exactly staging or production. A stray inherited DSN therefore cannot
 * ship local, test, CI, missing-env, or typo-env errors.
 *
 * The admin SPA has no dedicated Sentry project yet, and the staging build
 * already inlines the web `VITE_SENTRY_DSN` into the admin bundle — so admin
 * events land in the shared `coach-web` project. We tag every event
 * `app: 'admin'` via `initialScope` so admin errors stay filterable there.
 * A dedicated `coach-admin` project (+ a per-SPA DSN passthrough) is a future
 * option if admin volume or separation warrants it.
 */
export function initSentryBrowser(opts: InitSentryBrowserOptions): boolean {
  const dsn = opts.dsn?.trim()
  if (!dsn) return false
  if (opts.environment !== 'staging' && opts.environment !== 'production') {
    return false
  }
  Sentry.init({
    dsn,
    environment: opts.environment,
    release: opts.release,
    tracesSampleRate: opts.environment === 'production' ? 0.1 : 1.0,
    // Distinguish admin events within the shared coach-web project.
    initialScope: { tags: { app: 'admin' } },
    ...(opts.apiBaseUrl
      ? {
          integrations: [Sentry.browserTracingIntegration()],
          tracePropagationTargets: [opts.apiBaseUrl],
        }
      : {}),
  })
  return true
}
